/*
  Smoothly animates a native <details> accordion open AND close.
  Native <details> hides content instantly on close, so the height is animated
  via the Web Animations API on a padding-free wrapper ([data-accordion-motion])
  whose height can reach a true 0 without clamping.

  Markup:
    <accordion-motion>
      <details>
        <summary>…</summary>
        <div data-accordion-motion>…content…</div>
      </details>
    </accordion-motion>
*/
(() => {
  if (customElements.get('accordion-motion')) return;

  const DURATION = 300;

  class AccordionMotion extends HTMLElement {
    constructor() {
      super();
      this.onSummaryClick = this.onSummaryClick.bind(this);
      this.animation = null;
    }

    connectedCallback() {
      this.details = this.querySelector('details');
      this.summary = this.querySelector('summary');
      this.motion = this.querySelector('[data-accordion-motion]');
      if (!this.details || !this.summary || !this.motion) return;
      this.summary.addEventListener('click', this.onSummaryClick);
    }

    disconnectedCallback() {
      this.summary?.removeEventListener('click', this.onSummaryClick);
    }

    get reducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    onSummaryClick(event) {
      event.preventDefault();
      if (this.reducedMotion) {
        this.details.open = !this.details.open;
        return;
      }
      if (this.animation) this.animation.cancel();
      if (this.details.open) {
        this.shrink();
      } else {
        this.expand();
      }
    }

    expand() {
      this.details.open = true;
      this.run(0, this.motion.scrollHeight);
    }

    shrink() {
      this.run(this.motion.scrollHeight, 0, () => {
        this.details.open = false;
      });
    }

    run(from, to, onFinish) {
      this.motion.style.overflow = 'hidden';
      this.animation = this.motion.animate(
        { height: [`${from}px`, `${to}px`] },
        { duration: DURATION, easing: 'ease' }
      );
      const cleanup = () => {
        this.motion.style.overflow = '';
        this.motion.style.height = '';
        this.animation = null;
      };
      this.animation.onfinish = () => {
        cleanup();
        if (onFinish) onFinish();
      };
      this.animation.oncancel = cleanup;
    }
  }

  customElements.define('accordion-motion', AccordionMotion);
})();
